import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Statistic, Row, Col, Tag, Divider, Spin, Alert, Typography } from 'antd';
import { CheckCircleOutlined, TrophyOutlined, RiseOutlined, FallOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * DAS-1662: Modal to display rebalancing selection details
 * Shows WHY a specific ticker was selected during rebalancing
 */
const RebalancingSelectionModal = ({ open, onClose, runId, ticker, rebalancingDate, callAction }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && runId && ticker && rebalancingDate) {
      fetchSelectionDetails();
    }
  }, [open, runId, ticker, rebalancingDate]);

  const fetchSelectionDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await callAction('get_rebalancing_selection_details', {
        run_id: runId,
        ticker: ticker,
        rebalancing_date: rebalancingDate
      });

      if (response?.success && response?.data) {
        setData(response.data);
      } else {
        setError(response?.error || 'Failed to fetch selection details');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(decimals);
  };

  const getZScoreColor = (zscore) => {
    if (zscore === null || zscore === undefined) return 'default';
    if (zscore > 2) return 'green';
    if (zscore > 1) return 'blue';
    if (zscore < -2) return 'red';
    if (zscore < -1) return 'orange';
    return 'default';
  };

  const getZScoreLabel = (zscore, factor) => {
    if (zscore === null || zscore === undefined) return 'N/A';
    const abs = Math.abs(zscore);
    let label = '';
    
    if (abs > 2) label = 'Extreme';
    else if (abs > 1) label = 'High';
    else if (abs > 0.5) label = 'Moderate';
    else label = 'Average';

    if (factor === 'ROA') {
      return zscore > 0 ? `${label} (Strong)` : `${label} (Weak)`;
    } else { // PB
      return zscore > 0 ? `${label} (Expensive)` : `${label} (Cheap)`;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading selection details...</div>
        </div>
      );
    }

    if (error) {
      return <Alert message="Error" description={error} type="error" showIcon />;
    }

    if (!data) {
      return <Alert message="No data available" type="info" showIcon />;
    }

    const topNPercent = 100 - (data.percentile || 0);
    const globalPercentile = ((data.total_universe_size - data.global_rank) / data.total_universe_size * 100).toFixed(1);
    const sectorPercentile = data.sector_rank && data.sector_size 
      ? ((data.sector_size - data.sector_rank) / data.sector_size * 100).toFixed(1)
      : null;

    return (
      <>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 8 }}>
            <TrophyOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            {ticker}
          </Title>
          <Text type="secondary">Selected on {rebalancingDate}</Text>
        </div>

        {/* Composite Score */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Statistic
              title="Composite Score"
              value={formatNumber(data.composite_score, 4)}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Global Rank"
              value={`#${data.global_rank}`}
              suffix={`/ ${data.total_universe_size}`}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Top {globalPercentile}%
            </Text>
          </Col>
          <Col span={8}>
            <Statistic
              title="Selection Criteria"
              value={`Top ${topNPercent}%`}
              valueStyle={{ color: '#722ed1' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data.sector_neutral ? 'Sector-Neutral' : 'Cross-Sectional'}
            </Text>
          </Col>
        </Row>

        <Divider />

        {/* Sector Info - only show full details for sector-neutral mode */}
        <Title level={5}>Sector Analysis</Title>
        <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Sector" span={2}>
            <Tag color="blue">{data.sector || 'Unknown'}</Tag>
          </Descriptions.Item>
          {data.sector_neutral ? (
            <>
              <Descriptions.Item label="Sector Rank">
                #{data.sector_rank} / {data.sector_size}
                {sectorPercentile && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    (Top {sectorPercentile}%)
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Selected from Sector">
                {data.sector_top_n} tickers
              </Descriptions.Item>
            </>
          ) : (
            <Descriptions.Item label="Selection Mode" span={2}>
              <Tag color="purple">Cross-Sectional</Tag>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                Selected from global ranking (not per-sector)
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider />

        {/* Factor Breakdown */}
        <Title level={5}>Factor Breakdown</Title>
        <Alert
          message={(() => {
            // DAS-1696: Display actual factor formula based on configuration
            const preset = data.factor_preset || 'value';
            const roaW = data.roa_weight ?? 50;
            const pbW = data.pb_weight ?? 50;
            
            if (preset === 'growth' || pbW === 0) {
              return 'Formula: Composite Score = 1.0 × ROA_z';
            }
            if (preset === 'value' || (roaW === 50 && pbW === 50)) {
              return 'Formula: Composite Score = 0.5 × ROA_z - 0.5 × PB_z';
            }
            // Custom weights
            const roaFrac = (roaW / 100).toFixed(2);
            const pbFrac = (pbW / 100).toFixed(2);
            return `Formula: Composite Score = ${roaFrac} × ROA_z - ${pbFrac} × PB_z`;
          })()}
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Z-scores baseline: <strong>{data.z_score_baseline === 'market' ? 'Market (sector benchmarks)' : 'Universe (on-the-fly)'}</strong>
            </Text>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* ROA */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label={<><RiseOutlined /> ROA (Return on Assets)</>}>
                <strong>{formatPercent(data.roa)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="ROA Z-Score">
                <Tag color={getZScoreColor(data.roa_zscore)}>
                  {formatNumber(data.roa_zscore, 2)}
                </Tag>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {getZScoreLabel(data.roa_zscore, 'ROA')}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>

          {/* P/B */}
          <Col span={12}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label={<><FallOutlined /> Price-to-Book (P/B)</>}>
                <strong>{formatNumber(data.pb, 2)}x</strong>
              </Descriptions.Item>
              <Descriptions.Item label="P/B Z-Score">
                <Tag color={getZScoreColor(-data.pb_zscore)}>
                  {formatNumber(data.pb_zscore, 2)}
                </Tag>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {getZScoreLabel(data.pb_zscore, 'PB')}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        {/* DAS-1762: Momentum Filter Info */}
        {data.momentum_value !== null && data.momentum_value !== undefined && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label={<><ThunderboltOutlined /> Momentum (12-1)</>}>
                  <Tag color={data.momentum_value >= 0.05 ? 'green' : data.momentum_value >= 0 ? 'blue' : 'red'}>
                    {(data.momentum_value * 100).toFixed(1)}%
                  </Tag>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {data.momentum_value >= 0.05 ? '✅ Passed filter' : data.momentum_value >= 0 ? '✅ Positive' : '❌ Negative'}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Filter Status">
                  <Text type="secondary">
                    12-month return (skipping last month) • Min threshold: 5%
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        )}

        {/* Explanation - different text for sector-neutral vs cross-sectional */}
        <Alert
          message="Why was this ticker selected?"
          description={
            <div>
              {data.sector_neutral ? (
                <>
                  <p>
                    <strong>{ticker}</strong> was selected because it ranked{' '}
                    <strong>#{data.sector_rank}</strong> out of <strong>{data.sector_size}</strong>{' '}
                    tickers in the <strong>{data.sector}</strong> sector.
                  </p>
                  <p>
                    The rebalancing strategy selected the top <strong>{topNPercent}%</strong> from each sector,
                    which meant choosing the top <strong>{data.sector_top_n}</strong> ticker(s) from this sector.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>{ticker}</strong> was selected because it ranked{' '}
                    <strong>#{data.global_rank}</strong> out of <strong>{data.total_universe_size}</strong>{' '}
                    tickers globally (cross-sectional selection).
                  </p>
                  <p>
                    The rebalancing strategy selected the top <strong>{topNPercent}%</strong> of all stocks,
                    regardless of sector. {ticker} is in the <strong>{data.sector}</strong> sector.
                  </p>
                </>
              )}
              <p>
                {ticker} had a high composite score of <strong>{formatNumber(data.composite_score, 4)}</strong>{' '}
                due to:
              </p>
              <ul>
                <li>
                  <strong>ROA:</strong> {formatPercent(data.roa)} (z-score: {formatNumber(data.roa_zscore, 2)}) -{' '}
                  {data.roa_zscore > 1 ? 'significantly above' : data.roa_zscore > 0 ? 'above' : 'below'}{' '}
                  {data.z_score_baseline === 'market' ? `${data.sector} sector benchmark` : 'universe'} average
                </li>
                <li>
                  <strong>P/B:</strong> {formatNumber(data.pb, 2)}x (z-score: {formatNumber(data.pb_zscore, 2)}) -{' '}
                  {data.pb_zscore < -1 ? 'significantly undervalued' : data.pb_zscore < 0 ? 'undervalued' : 'overvalued'}{' '}
                  relative to {data.z_score_baseline === 'market' ? `${data.sector} sector benchmark` : 'universe'}
                </li>
              </ul>
            </div>
          }
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
        />
      </>
    );
  };

  return (
    <Modal
      title="Rebalancing Selection Details"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default RebalancingSelectionModal;

